import React, { useState, useEffect } from "react";
import { useHistory } from "react-router";
import LocalStorage from "../../../../hook/LocalStorage";
import logo from "../../../../assets/images/logo/Haylo-for-white-background.png";
import http from "../../../../config/BaseUrl";
import Loader from "../../../../components/Loader/Loader";
import { BsArrowLeft } from "react-icons/bs";
import PricingTableHeader from "./PricingTableHeader";
import PricingTableTab from "./PricingTableTab";

const PACKAGE_ITEMS = [
  { name: "Trial Package", type: "trial", tabId: "subscription-props-trial", targetTab: "subscription-props-four" },
  { name: "Single Package", type: "single", tabId: "subscription-props-single", targetTab: "subscription-props-three" },
  { name: "Monthly", type: "monthly", tabId: "subscription-props-monthly", targetTab: "subscription-props-one" },
  { name: "Yearly", type: "yearly", tabId: "subscription-props-yearly", targetTab: "subscription-props-two" },
];

const getPackageType = (packageValue) => {
  return packageValue?.our_package?.package_type?.type ||
    packageValue?.our_package?.package_type ||
    packageValue?.package_type;
};

const groupPackageCatalog = (catalog, includeTrial) => {
  if (!Array.isArray(catalog)) {
    throw new Error("Package catalogue is unavailable.");
  }

  const groupByType = (type) => catalog.map((item) => ({
    item_name: item?.item_name || "Package feature",
    itemData: Array.isArray(item?.package_item_value)
      ? item.package_item_value.filter((packageValue) => getPackageType(packageValue) === type)
      : [],
  }));

  return {
    monthlyData: groupByType("monthly"),
    yearlyData: groupByType("yearly"),
    singleData: groupByType("single"),
    trialData: includeTrial ? groupByType("trial") : [],
  };
};

const EventType = () => {
  const history = useHistory();
  const { access_token, user } = LocalStorage();
  const membership = user?.membership;
  const hasMembership = Boolean(membership);
  const currentPackage = membership?.our_package;
  const currentPackageId = currentPackage?.id ?? membership?.package_id;
  const currentPackageType = currentPackage?.package_type?.type || membership?.package_type;

  const [packageTypes, setPackageTypes] = useState(null);
  const [selectType, setSelectType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [requestKey, setRequestKey] = useState(0);

  const packageSelect = (data) => {
    setSelectType({
      type: data.billed,
      package_id: data.package,
    });

    if (data.billed === "single" || data.billed === "trial") {
      setLoading(true);
      const formData = {
        user_id: user?.id,
        user_email: user?.email,
        submittedData: data,
      };
      const configToken = {
        headers: { Authorization: `Bearer ${access_token}` },
      };

      http.post("member-ship", formData, configToken)
        .then((res) => {
          setLoading(false);
          if (res.data.card_info) {
            localStorage.setItem("users", JSON.stringify(res.data.user));
            history.push("/dashboard");
          } else {
            history.goBack();
          }
        })
        .catch(() => {
          setLoading(false);
          setLoadError("Unable to process the selected package. Please try again.");
        });
    } else if (access_token) {
      history.push("/event/payment", { package: data });
    } else {
      history.go("/event/payment", { package: data });
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadPackages = async () => {
      setLoading(true);
      setLoadError("");

      if (currentPackageId !== null && currentPackageId !== undefined) {
        setSelectType({
          type: currentPackageType === "trial" ? "single" : currentPackageType || "single",
          package_id: currentPackageId,
        });
      }

      try {
        const res = await http.get("/package-type");
        const packageData = groupPackageCatalog(res?.data?.packageType, !hasMembership);

        if (mounted) {
          setPackageTypes(packageData);
        }
      } catch (error) {
        if (mounted) {
          setPackageTypes(null);
          setLoadError("Unable to load package options. Please try again.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadPackages();

    return () => {
      mounted = false;
    };
  }, [currentPackageId, currentPackageType, hasMembership, requestKey]);

  const checkActiveTab = (tabName) => {
    if (selectType) {
      if (tabName === selectType.type) {
        return true;
      } else {
        return false;
      }
    } else {
      if (tabName === "single") {
        return true;
      } else {
        return false;
      }
    }
  };

  const checkActivePackage = (packageId) => {
    if (selectType) {
      if (String(selectType.package_id) === String(packageId)) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  return (
    <>
      {!loading ? (
        <section className="pb-5">
          <div className="container">
            <div className="my-5 row justify-content-center">
              <div className="text-center col-12 col-md-10">
                <div className="pb-4 heading">
                  <div className="page-logo">
                    <img
                      src={logo}
                      className="img-fluid"
                      width="200"
                      alt="event type"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="row justify-content-center">
              <div className="col-md-12">
                <div
                  className="back-to-previous-page"
                  onClick={() => history.goBack()}
                >
                  <span>
                    <BsArrowLeft />
                  </span>
                  <span>Go Back</span>
                </div>
              </div>
              {loadError ? (
                <div className="col-md-12">
                  <div className="alert alert-warning d-flex justify-content-between align-items-center" role="alert">
                    <span>{loadError}</span>
                    <button type="button" className="ml-3 btn btn-sm btn-outline-dark" onClick={() => setRequestKey((key) => key + 1)}>
                      Retry
                    </button>
                  </div>
                </div>
              ) : null}
              <div className="col-md-12">
                <div className="pricing-header">
                  <ul
                    className="mb-3 nav nav-pills justify-content-center pricing-items align-items-center"
                    id="subscription-tab"
                    role="tablist"
                  >
                    {
                      PACKAGE_ITEMS.map((item, index) => (
                        <PricingTableHeader
                          key={`${item.type}-tab-item-${index}`}
                          checkActiveTab={checkActiveTab}
                          tabId={item.tabId}
                          name={item.name}
                          type={item.type}
                          targetTab={item.targetTab}
                          packages={
                            packageTypes && (
                              item.type === "trial" ?
                              packageTypes.trialData
                              : item.type === "single" ?
                              packageTypes.singleData
                              : item.type === "monthly" ?
                              packageTypes.monthlyData
                              : packageTypes.yearlyData)
                          }
                        />
                      ))
                    }
                    
                  </ul>
                </div>
                <div
                  className="pricing-table tab-content"
                  id="subscription-props-tabContent"
                >

                  {PACKAGE_ITEMS.map((item, index) => (
                    <PricingTableTab
                      key={`${item.type}-tab-item-${index}`}
                      checkActiveTab={checkActiveTab}
                      type={item.type}
                      targetTab={item.targetTab}
                      packages={
                        packageTypes && (
                          item.type === "trial" ?
                          packageTypes.trialData
                          : item.type === "single" ?
                          packageTypes.singleData
                          : item.type === "monthly" ?
                          packageTypes.monthlyData
                          : packageTypes.yearlyData)
                      }
                      packageSelect={packageSelect}
                      checkActivePackage={checkActivePackage}
                    />
                  ))}

                  
                </div>
              </div>
            </div>

            <div className="row justify-content-center">
              <div className="col-md-6 mt-5">
                <div className="footer-top">
                  <img
                    src={logo}
                    className="img-fluid"
                    alt="footer-icon"
                    width="150"
                  />
                </div>

                <div className="footer-bottom">
                  <p>
                    <span className="icon-footer">
                      <i className="lni lni-world"></i>
                    </span>
                    www.thehaylo.com.au
                  </p>
                  <p>
                    <span className="icon-footer">
                      <i className="lni lni-envelope"></i>
                    </span>
                    info.thehaylo.com.au
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default EventType;
